// MDP-06: browser boundary for the official-Python IBKR chart service.
// This route deliberately has a single authority: it never falls back to a
// legacy bridge, cached candles, or another market-data provider.
import { supabase } from '../supabase.js'
import { subscribeBrowserResume } from './browserResume.js'

export const IBKR_CHART_TIMEFRAMES = Object.freeze([
  '1min', '2min', '5min', '15min', '30min', 'daily', 'weekly',
])

const INTRADAY_TIMEFRAMES = new Set(['1min', '2min', '5min', '15min', '30min'])
// The reconcile callback retains chart identity across ticket rotation.
const resumeRecoveryTimes = new WeakMap()
const RECOVERY_RETRY_DELAYS_MS = Object.freeze([1000, 2000, 5000])

export const IBKR_CHART_CONNECTION_STATES = Object.freeze({
  CONNECTING: 'connecting',
  LIVE: 'live',
  RECONNECTING: 'reconnecting',
  RECOVERING: 'recovering',
  UNAVAILABLE: 'unavailable',
})

function normalizeChartContract(contract) {
  const secType = String(contract?.secType || 'STK').trim().toUpperCase()
  if (secType === 'STK') return null
  if (secType !== 'OPT') throw new Error('Unsupported IBKR chart security type')
  const expiry = String(contract?.expiry || '').trim()
  const strike = Number(contract?.strike)
  const right = String(contract?.right || '').trim().toUpperCase()
  if (!/^\d{8}$/.test(expiry) || !Number.isFinite(strike) || strike <= 0 || !['C', 'P'].includes(right)) {
    throw new Error('Option contract requires expiry, strike, and C/P right')
  }
  const conid = contract?.conid == null ? null : Number(contract.conid)
  if (conid != null && (!Number.isSafeInteger(conid) || conid <= 0)) throw new Error('Invalid option contract conId')
  const exchange = String(contract?.exchange || 'SMART').trim().toUpperCase()
  const currency = String(contract?.currency || 'USD').trim().toUpperCase()
  const multiplier = contract?.multiplier == null ? null : String(contract.multiplier).trim()
  return {
    secType, expiry, strike, right, exchange, currency,
    ...(conid == null ? {} : { conid }),
    ...(multiplier ? { multiplier } : {}),
  }
}

export function normalizeIbkrChartRequest({ symbol, timeframe, session = 'regular', before = null, contract = null } = {}) {
  const normalizedSymbol = String(symbol || '').trim().toUpperCase()
  if (!/^[A-Z][A-Z0-9.\-]{0,14}$/.test(normalizedSymbol)) throw new Error('Invalid chart symbol')
  if (!IBKR_CHART_TIMEFRAMES.includes(timeframe)) throw new Error(`Unsupported IBKR chart timeframe: ${timeframe}`)
  const normalizedSession = session === 'all' ? 'all' : 'regular'
  if (!INTRADAY_TIMEFRAMES.has(timeframe) && normalizedSession !== 'regular') {
    throw new Error('Daily and weekly charts are RTH-only')
  }
  if (before != null && (!Number.isFinite(Number(before)) || Number(before) <= 0)) {
    throw new Error('Invalid chart history cursor')
  }
  return {
    symbol: normalizedSymbol,
    timeframe,
    session: normalizedSession,
    before: before == null ? null : Number(before),
    contract: normalizeChartContract(contract),
  }
}

// One formatter per options set, not per candle in a history response.
const historyFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
})

function normalizeRawBar(bar, timeframe) {
  let time = bar?.time
  const fields = ['open', 'high', 'low', 'close']
  if (time == null || fields.some((field) => bar?.[field] == null || bar[field] === '' || typeof bar[field] === 'boolean' || !Number.isFinite(Number(bar[field])))) return null
  if (Number(time) > 1_000_000_000) {
    const date = new Date(Number(time) * 1000)
    if (!Number.isFinite(date.getTime())) return null
    const parts = historyFormatter.formatToParts(date)
    const part = (type) => parts.find((value) => value.type === type)?.value
    const day = `${part('year')}${part('month')}${part('day')}`
    time = timeframe === 'daily' || timeframe === 'weekly'
      ? day
      : `${day} ${part('hour') === '24' ? '00' : part('hour')}:${part('minute')}:${part('second')} US/Eastern`
  }
  const volume = Number(bar.volume)
  return {
    time,
    open: Number(bar.open), high: Number(bar.high), low: Number(bar.low), close: Number(bar.close),
    volume: Number.isFinite(volume) && volume >= 0 ? volume : 0,
  }
}

async function browserAuthorization() {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw Object.assign(new Error('Sign in again to load chart data'), { code: 'CHART_AUTH' })
  return `Bearer ${session.access_token}`
}

async function fetchIbkrChartPayload(requestInput, { snapshotOnly = false, signal, requestScope, requestGeneration, purpose, fetchImpl = globalThis.fetch } = {}) {
  const request = normalizeIbkrChartRequest(requestInput)
  const params = new URLSearchParams({ symbol: request.symbol, timeframe: request.timeframe, session: request.session })
  if (request.contract) {
    for (const [field, value] of Object.entries(request.contract)) params.set(field, String(value))
  }
  if (request.before != null) params.set('before', String(request.before))
  if (snapshotOnly) params.set('snapshotOnly', '1')
  if (requestScope != null) { params.set('requestScope', requestScope); params.set('requestGeneration', String(requestGeneration)) }
  if (purpose) params.set('purpose', purpose)
  signal?.throwIfAborted()
  const authorization = await browserAuthorization()
  signal?.throwIfAborted()
  const response = await fetchImpl(`/api/ibkr/chart/history?${params}`, {
    headers: { Authorization: authorization }, signal,
  })
  if (!response?.ok) throw Object.assign(new Error(`IBKR chart ${response?.status || 'network error'}`), { code: response?.status === 401 ? 'CHART_AUTH' : 'CHART_REQUEST_FAILED' })
  const payload = await response.json()
  signal?.throwIfAborted()
  if (!Array.isArray(payload?.bars)) throw Object.assign(new Error('Invalid IBKR chart response'), { code: 'CHART_REQUEST_FAILED' })
  const bars = payload.bars
    .map((bar) => normalizeRawBar(bar, request.timeframe)).filter(Boolean)
  if (payload.bars.length && bars.length !== payload.bars.length) throw Object.assign(new Error('Invalid IBKR chart bars'), { code: 'CHART_REQUEST_FAILED' })
  if (!bars.length) throw Object.assign(new Error('No IBKR chart data'), { code: 'CHART_EMPTY' })
  return { bars, ...(typeof payload?.streamUrl === 'string' && payload.streamUrl ? { streamUrl: payload.streamUrl } : {}) }
}

export async function fetchIbkrChartHistory(requestInput, options = {}) {
  const { bars } = await fetchIbkrChartPayload(requestInput, { ...options, snapshotOnly: true })
  return bars
}

export async function fetchIbkrChartSnapshot(requestInput, options = {}) {
  const result = await fetchIbkrChartPayload(requestInput, options)
  if (!result.streamUrl) throw new Error('IBKR chart stream is unavailable')
  return result
}

export function subscribeIbkrChartStream({ streamUrl, timeframe, reconcile, onFormingCandle, onCompletedCandle, onPrice, onState, onError, eventSourceFactory = (url) => new EventSource(url), networkEventTarget = globalThis, subscribeResume = subscribeBrowserResume } = {}) {
  if (!streamUrl) throw new Error('IBKR chart stream URL is required')
  const source = eventSourceFactory(streamUrl)
  let active = true
  let recoveryGeneration = 0
  let recoveryAttempt = 0
  let recoveryInFlight = false
  let recoveryTimer = null
  let browserOffline = false
  let volumeCheckArmed = false
  const recentVolumes = new Map()
  let lifecycle = IBKR_CHART_CONNECTION_STATES.CONNECTING

  const transition = (state) => {
    lifecycle = state
    onState?.({ state })
  }

  const clearRecoveryTimer = () => {
    if (recoveryTimer == null) return
    clearTimeout(recoveryTimer)
    recoveryTimer = null
  }

  const scheduleRecovery = (generation, delayMs) => {
    if (!active || generation !== recoveryGeneration) return
    clearRecoveryTimer()
    recoveryTimer = setTimeout(() => {
      recoveryTimer = null
      void recover(generation)
    }, delayMs)
  }

  const recover = async (generation) => {
    if (!active || generation !== recoveryGeneration || recoveryInFlight) return
    recoveryInFlight = true
    transition(IBKR_CHART_CONNECTION_STATES.RECOVERING)
    try {
      if (typeof reconcile !== 'function') throw new Error('IBKR chart stream reconciliation is unavailable')
      const recovery = await reconcile({
        isCurrent: () => active && generation === recoveryGeneration,
      })
      if (!active || generation !== recoveryGeneration) return
      // A refreshed history request normally issues a new short-lived ticket.
      // Keep this source fail-closed until React replaces it with that source;
      // the new subscription's first open becomes the truthful live boundary.
      if (typeof recovery?.streamUrl === 'string' && recovery.streamUrl !== streamUrl) return
      recoveryAttempt = 0
      transition(IBKR_CHART_CONNECTION_STATES.LIVE)
    } catch (error) {
      if (!active || generation !== recoveryGeneration) return
      transition(IBKR_CHART_CONNECTION_STATES.RECONNECTING)
      onError?.(error instanceof Error ? error : new Error('IBKR chart stream recovery failed'))
      const delay = RECOVERY_RETRY_DELAYS_MS[Math.min(recoveryAttempt, RECOVERY_RETRY_DELAYS_MS.length - 1)]
      recoveryAttempt += 1
      scheduleRecovery(generation, delay)
    } finally {
      recoveryInFlight = false
    }
  }

  const handleOpen = async () => {
    if (!active) return
    if (lifecycle === IBKR_CHART_CONNECTION_STATES.CONNECTING) {
      recoveryAttempt = 0
      transition(IBKR_CHART_CONNECTION_STATES.LIVE)
      return
    }
    if (lifecycle !== IBKR_CHART_CONNECTION_STATES.RECONNECTING
      && lifecycle !== IBKR_CHART_CONNECTION_STATES.UNAVAILABLE) return
    clearRecoveryTimer()
    await recover(recoveryGeneration)
  }

  const handleResume = () => {
    if (!active || browserOffline || recoveryInFlight || lifecycle === IBKR_CHART_CONNECTION_STATES.RECOVERING) return
    // Focus, visibility, pageshow and the sleep watchdog can describe one
    // return. Coalesce them instead of rotating several history tickets.
    const resumeKey = typeof reconcile === 'function' ? reconcile : source
    if (Date.now() - (resumeRecoveryTimes.get(resumeKey) ?? -Infinity) < 5000) return
    resumeRecoveryTimes.set(resumeKey, Date.now())
    void handleError()
  }

  const checkVolumeContinuity = (candle, completed = false) => {
    if (!INTRADAY_TIMEFRAMES.has(timeframe) || typeof candle.time !== 'string') return
    const latestTime = [...recentVolumes.keys()].at(-1)
    if (latestTime && candle.time.slice(0, 8) !== latestTime.slice(0, 8)) {
      // Do not compare different sessions or let a late prior-day correction
      // reset the active day's evidence.
      if (candle.time < latestTime) return
      recentVolumes.clear()
      volumeCheckArmed = false
    }
    if (!completed && latestTime && candle.time < latestTime) return
    if (completed && !recentVolumes.has(candle.time)) return
    recentVolumes.set(candle.time, candle.volume)
    const ordered = [...recentVolumes.entries()].sort(([a], [b]) => a.localeCompare(b)).slice(-3)
    recentVolumes.clear()
    for (const [time, volume] of ordered) recentVolumes.set(time, volume)
    if (candle.volume > 0 && candle.time === ordered.at(-1)?.[0]) volumeCheckArmed = true
    // One forming zero is normal. Two closed zero buckets after previously
    // observed volume warrant one authoritative check, not invented volume.
    // Re-arm only when positive volume returns, avoiding a zero-data loop.
    if (volumeCheckArmed && ordered.length === 3 && ordered.slice(0, 2).every(([, volume]) => volume === 0)) {
      volumeCheckArmed = false
      void handleError()
    }
  }

  const handleMessage = (event) => {
    if (!active || lifecycle !== IBKR_CHART_CONNECTION_STATES.LIVE) return
    let payload
    try { payload = JSON.parse(event.data) } catch { return }
    if (payload?.type === 'forming_candle' && payload.candle) {
      // History normalizes the Python service's UTC epoch into IBKR's
      // established timestamp shape before `toCandles` converts it into the
      // chart's display-time coordinate. Apply the same normalization here:
      // otherwise a live UTC epoch is misread as a YYYYMMDD value and writes
      // beside, rather than into, the visible forming candle.
      const candle = normalizeRawBar(payload.candle, timeframe)
      if (candle) {
        onFormingCandle?.(candle, payload)
        checkVolumeContinuity(candle)
      }
    }
    if (payload?.type === 'completed_candle' && payload.candle) {
      const candle = normalizeRawBar(payload.candle, timeframe)
      if (candle) {
        onCompletedCandle?.(candle, payload)
        checkVolumeContinuity(candle, true)
      }
    }
    if (payload?.type === 'price' && Number.isFinite(Number(payload?.price))) onPrice?.(payload, payload)
    if (payload?.type === 'state') onState?.(payload)
  }

  const handleError = () => {
    if (!active) return
    if (lifecycle === IBKR_CHART_CONNECTION_STATES.LIVE
      || lifecycle === IBKR_CHART_CONNECTION_STATES.CONNECTING) {
      recoveryGeneration += 1
      recoveryAttempt = 0
    }
    transition(IBKR_CHART_CONNECTION_STATES.RECONNECTING)
    onError?.(new Error('IBKR chart stream unavailable'))
    if (browserOffline) return Promise.resolve()
    if (recoveryInFlight) {
      scheduleRecovery(recoveryGeneration, 0)
      return Promise.resolve()
    }
    return recover(recoveryGeneration)
  }

  const handleOffline = () => {
    if (!active) return
    browserOffline = true
    clearRecoveryTimer()
    if (lifecycle === IBKR_CHART_CONNECTION_STATES.LIVE
      || lifecycle === IBKR_CHART_CONNECTION_STATES.CONNECTING) {
      recoveryGeneration += 1
      recoveryAttempt = 0
    }
    transition(IBKR_CHART_CONNECTION_STATES.RECONNECTING)
    onError?.(new Error('Browser network unavailable'))
  }

  const handleOnline = () => {
    if (!active) return Promise.resolve()
    browserOffline = false
    if (lifecycle !== IBKR_CHART_CONNECTION_STATES.RECONNECTING
      && lifecycle !== IBKR_CHART_CONNECTION_STATES.RECOVERING
      && lifecycle !== IBKR_CHART_CONNECTION_STATES.UNAVAILABLE) return Promise.resolve()
    clearRecoveryTimer()
    if (recoveryInFlight) {
      scheduleRecovery(recoveryGeneration, 0)
      return Promise.resolve()
    }
    return recover(recoveryGeneration)
  }

  source.addEventListener('open', handleOpen)
  source.addEventListener('message', handleMessage)
  source.addEventListener('error', handleError)
  networkEventTarget?.addEventListener?.('offline', handleOffline)
  networkEventTarget?.addEventListener?.('online', handleOnline)
  const stopResume = subscribeResume(handleResume)
  onState?.({ state: lifecycle })

  return () => {
    if (!active) return
    active = false
    recoveryGeneration += 1
    clearRecoveryTimer()
    source.removeEventListener?.('open', handleOpen)
    source.removeEventListener?.('message', handleMessage)
    source.removeEventListener?.('error', handleError)
    networkEventTarget?.removeEventListener?.('offline', handleOffline)
    networkEventTarget?.removeEventListener?.('online', handleOnline)
    stopResume?.()
    source.close()
  }
}
